import { request } from '@/common/ajax/request';
import Vue from 'vue';

const CATEGORIES = Object.freeze(['purposes', 'specialPurposes', 'features', 'specialFeatures', 'vendors']);

// eslint-disable-next-line no-unused-vars
const CATEGORIES_WITH_CONSENT = Object.freeze(['purposes', 'specialFeatures', 'vendors', 'stacks']);

const VENDORS_OFFSET = 1000;
const PURPOSES_OFFSET = 10000;
const SPECIAL_PURPOSES_OFFSET = 11000;
const FEATURES_OFFSET = 12000;
const SPECIAL_FEATURES_OFFSET = 13000;

export class Iab2 {
  constructor(options) {
    this.vendorListUrl = options.url;
    this.vendorListBaseUrl = options.baseUrl || 'https://cdn.trustcommander.net/iab-v2';
    this.vendorList = {};
    /**
     * Processing of the vendor list offsets the id of the categories from the vendor list, and
     * these categories are mapped with the resulting id. Additionally, the processing
     * inits the categories with properties to manage the consent value.
     */
    this.processedVendorList = {
      vendors: {},
      purposes: {},
      specialPurposes: {},
      features: {},
      specialFeatures: {},
    };
    this.display = {};
    this.initDisplay();
    this.lastItemToDisplay = {};
  }

  initDisplay() {
    this.display.purposes = [];
    this.display.features = [];
    this.display.specialFeatures = [];
    this.display.specialPurposes = [];
    this.display.vendors = [];
    this.display.stacks = [];
  }

  resolveIabCategoryFromOffsetId(id) {
    id = parseInt(id);
    if (id >= PURPOSES_OFFSET && id < SPECIAL_PURPOSES_OFFSET) {
      return 'purposes';
    }
    if (id >= SPECIAL_PURPOSES_OFFSET && id < FEATURES_OFFSET) {
      return 'specialPurposes';
    }
    if (id >= FEATURES_OFFSET && id < SPECIAL_FEATURES_OFFSET) {
      return 'features';
    }
    if (id >= SPECIAL_FEATURES_OFFSET) {
      return 'specialFeatures';
    }
    return null;
  }

  resolveOffsetFromId(id) {
    const category = this.resolveIabCategoryFromOffsetId(id);
    switch (category) {
      case 'purposes':
        return PURPOSES_OFFSET;
      case 'specialPurposes':
        return SPECIAL_PURPOSES_OFFSET;
      case 'features':
        return FEATURES_OFFSET;
      case 'specialFeatures':
        return SPECIAL_FEATURES_OFFSET;
      default:
        return 0;
    }
  }

  isLegitimateInterestId(offsetId) {
    return offsetId % 2 === 0 && offsetId < SPECIAL_PURPOSES_OFFSET;
  }

  idToConsentId(id, offset) {
    if ([PURPOSES_OFFSET, VENDORS_OFFSET].includes(offset)) {
      return this.idToLegitimateInterestId(id, offset) - 1;
    } else {
      return id + offset;
    }
  }

  offsetIdToConsentId(id) {
    if (!this.isLegitimateInterestId(id)) {
      return id;
    }
    return id - 1;
  }

  idToLegitimateInterestId(id, offset) {
    if ([PURPOSES_OFFSET, VENDORS_OFFSET].includes(offset)) {
      return id * 2 + offset;
    } else {
      return null;
    }
  }

  baseCategoryTraversal(fn, categoryNames, vendorList) {
    for (const categoryName of categoryNames) {
      const mainCategory = vendorList[categoryName];
      if (mainCategory) {
        if (categoryName !== 'stacks') {
          const categories = Object.entries(mainCategory);
          for (const [index, category] of categories) {
            const result = fn(category, index, categoryName);
            if (result === false) {
              return;
            }
          }
        } else {
          for (const stack of mainCategory) {
            this.baseCategoryTraversal(fn, categoryNames, stack);
          }
        }
      }
    }
  }

  categoryTraversal(fn, categoryNames = CATEGORIES, vendorList = this.vendorList) {
    this.baseCategoryTraversal(fn, categoryNames, vendorList);
  }

  processedCategoryTraversal(fn, categoryNames = CATEGORIES, vendorList = this.processedVendorList) {
    this.baseCategoryTraversal(fn, categoryNames, vendorList);
  }

  displayCategoryTraversal(fn, categoryNames = CATEGORIES, vendorList = this.display) {
    this.baseCategoryTraversal(fn, categoryNames, vendorList);
  }

  displayConsentCategoryTraversal(
    fn,
    categoryNames = CATEGORIES_WITH_CONSENT,
    vendorList = {
      purposes: this.display.purposes,
      specialFeatures: this.display.specialFeatures,
      vendors: this.display.vendors,
      stacks: this.display.stacks,
    }
  ) {
    this.baseCategoryTraversal(fn, categoryNames, vendorList);
  }

  async getVendorList(locale, { latest = false } = {}) {
    let translation;
    let vendorList;
    const vendorListUrl = latest ? `${this.vendorListBaseUrl}/vendor-list.json` : this.vendorListUrl;
    const vendorListPromise = request.getJson(vendorListUrl);
    if (locale !== 'en') {
      if (locale === 'pt') {
        // exception for Portuguese language, which is not encoded on 2 characters for IAB
        locale = 'pt-pt';
      }
      try {
        translation = await request.getJson(`${this.vendorListBaseUrl}/purposes-${locale}.json`);
      } catch (e) {
        console.error(`IAB potentially down or missing locale "${locale}", fallback to default locale "en". Error:`, e);
      }
    }
    try {
      vendorList = await vendorListPromise;
    } catch (e) {
      if (!latest) {
        return this.getVendorList(locale, { latest: true });
      }
      console.error('IAB Down', e);
      return;
    }

    if (translation) {
      translation.vendors = vendorList.vendors;
      vendorList = translation;
    }

    this.vendorList = vendorList;

    return this.vendorList;
  }

  partialOffsetId(vendorList) {
    return (property, offset, foreach) => {
      const result = {};
      const resolvedList = vendorList[property];

      for (const obj of Object.values(resolvedList)) {
        const consentId = this.idToConsentId(obj.id, offset);
        const legitimateInterestId = this.idToLegitimateInterestId(obj.id, offset);
        const newObj = Object.assign({}, obj, { category: property, consentId, legitimateInterestId });
        result[consentId] = newObj;
        if (foreach) {
          foreach(newObj);
        }
      }

      return result;
    };
  }

  processVendorList(vendorList, legIntDefaultValue) {
    const offsetId = this.partialOffsetId(vendorList);
    this.processedVendorList.purposes = offsetId('purposes', PURPOSES_OFFSET, purpose => {
      /** User values */
      Vue.set(purpose, 'consent', false);
      Vue.set(purpose, 'legitimateInterest', legIntDefaultValue);
      /** Metadata to know how the purpose will be used in the privacy center */
      Vue.set(purpose, 'isConsent', false);
      Vue.set(purpose, 'isLegitimateInterest', false);
      Vue.set(purpose, 'isInStack', false);
    });

    //Special case for the first purpose which has no legitimate interest possible
    delete this.processedVendorList.purposes[1 + PURPOSES_OFFSET].legitimateInterest;
    delete this.processedVendorList.purposes[1 + PURPOSES_OFFSET].legitimateInterestId;
    delete this.processedVendorList.purposes[2 + PURPOSES_OFFSET];

    this.processedVendorList.specialPurposes = offsetId('specialPurposes', SPECIAL_PURPOSES_OFFSET);
    this.processedVendorList.features = offsetId('features', FEATURES_OFFSET);
    this.processedVendorList.specialFeatures = offsetId('specialFeatures', SPECIAL_FEATURES_OFFSET, purpose => {
      Vue.set(purpose, 'consent', false);
    });

    for (const stack of Object.values(this.vendorList.stacks)) {
      stack.purposes = stack.purposes.map(
        id => this.processedVendorList.purposes[this.idToConsentId(id, PURPOSES_OFFSET)]
      );
      stack.specialFeatures = stack.specialFeatures.map(
        id => this.processedVendorList.specialFeatures[this.idToConsentId(id, SPECIAL_FEATURES_OFFSET)]
      );
    }
    this.processedVendorList.stacks = this.vendorList.stacks;
  }

  async initVendorList(locale, vendors, { displayLegInt, vendorDefaultOpts } = {}) {
    const vendorDefaultValue = vendorDefaultOpts === 'neutral' ? null : false;
    const legIntDefaultValue = displayLegInt === true;
    await this.getVendorList(locale);
    this.processVendorList(this.vendorList, legIntDefaultValue);
    this.processedVendorList.vendors = this.partialOffsetId({ vendors })('vendors', VENDORS_OFFSET, vendor => {
      Vue.set(vendor, 'consent', vendorDefaultValue);
      Vue.set(vendor, 'legitimateInterest', legIntDefaultValue || vendorDefaultValue);
      //Static flagging like purposes. No need for dynamic like in compute display 24/09/2020
      Vue.set(vendor, 'isConsent', vendor.purposes.length > 0 || vendor.specialFeatures.length > 0);
      Vue.set(vendor, 'isLegitimateInterest', vendor.legIntPurposes.length > 0 || vendor.specialPurposes.length > 0);
    });
  }

  getConsents(value = true) {
    const categories = [];
    const optinVendors = [];
    this.displayConsentCategoryTraversal((category, index, mainCategory) => {
      if (category.consent === value) {
        if (mainCategory === 'vendors') {
          if (category.purposes.length > 0 || category.specialFeatures.length > 0) {
            optinVendors.push(String(category.consentId));
          }
        } else {
          if (category.isConsent) {
            categories.push(String(category.consentId));
          }
        }
      }

      if (category.legitimateInterest === value) {
        if (mainCategory === 'vendors') {
          if (category.legIntPurposes.length > 0 || category.specialPurposes.length > 0) {
            optinVendors.push(String(category.legitimateInterestId));
          }
        } else if (category.isLegitimateInterest) {
          categories.push(String(category.legitimateInterestId));
        }
      }
    });
    return { categories, optinVendors };
  }

  processNeutralVendors(displayLegInt = false) {
    //For neutral vendors, we optin if at least one of its purpose is optin
    for (const vendor of Object.values(this.display.vendors)) {
      if (vendor.consent === null && vendor.purposes.length > 0) {
        for (const purposeId of vendor.purposes) {
          const purpose = this.processedVendorList.purposes[this.idToConsentId(purposeId, PURPOSES_OFFSET)];
          if (purpose && purpose.consent) {
            vendor.consent = true;
            if (!displayLegInt && vendor.legIntPurposes.length > 0) {
              vendor.legitimateInterest = true;
            }
            break;
          }
        }
        for (const sfId of vendor.specialFeatures) {
          const purpose = this.processedVendorList.specialFeatures[this.idToConsentId(sfId, SPECIAL_FEATURES_OFFSET)];
          if (purpose && purpose.consent) {
            vendor.consent = true;
            break;
          }
        }
      }
      if (vendor.legitimateInterest === null && vendor.legIntPurposes.length > 0) {
        for (const legIntId of vendor.legIntPurposes) {
          const purpose = this.processedVendorList.purposes[this.idToConsentId(legIntId, PURPOSES_OFFSET)];
          if (purpose && purpose.legitimateInterest) {
            vendor.legitimateInterest = true;
            if (!displayLegInt && vendor.purposes.length > 0) {
              vendor.consent = true;
            }
            break;
          }
        }
      }
    }
  }

  setAllConsents(value, categories = CATEGORIES_WITH_CONSENT, displayLegInt = false) {
    this.displayConsentCategoryTraversal((category, index, categoryName) => {
      if (category.isConsent) {
        category.consent = value;
      }
      if (category.isLegitimateInterest && (categoryName === 'purposes' || categoryName === 'vendors')) {
        if (categoryName === 'purposes') {
          // initialize legintPurposes when separate toggle is used
          category.legitimateInterest =
            category.legitimateInterest !== undefined ? category.legitimateInterest : displayLegInt;
        }
      }
    }, categories);
  }

  reInitConsents({ hasCookie, optinCategories, optinVendors }) {
    if (!hasCookie) {
      return;
    }
    this.processedCategoryTraversal(c => {
      c.consent = false;
      c.legitimateInterest = false;
    });
    for (const categoryId of optinCategories) {
      const iabProperty = this.resolveIabCategoryFromOffsetId(categoryId);
      const consentId = this.offsetIdToConsentId(categoryId);
      if (iabProperty) {
        const category = this.processedVendorList[iabProperty][consentId];
        if (this.isLegitimateInterestId(categoryId)) {
          category.legitimateInterest = true;
        } else {
          category.consent = true;
        }
      }
    }
    for (const vendorId of optinVendors) {
      const consentId = this.offsetIdToConsentId(vendorId);
      const iabVendor = this.processedVendorList.vendors[consentId];
      if (iabVendor) {
        if (this.isLegitimateInterestId(vendorId)) {
          iabVendor.legitimateInterest = true;
        } else {
          iabVendor.consent = true;
        }
      }
    }
  }

  resetDisplay() {
    this.initDisplay();
    for (const purpose of Object.values(this.processedVendorList.purposes)) {
      purpose.isConsent = false;
      purpose.isLegitimateInterest = false;
      purpose.isInStack = false;
    }
    for (const sf of Object.values(this.processedVendorList.specialFeatures)) {
      sf.isInStack = false;
    }
  }

  computeDisplay(items) {
    this.lastItemToDisplay = items;
    const purposes = {};
    if (this.lastItemToDisplay) {
      this.resetDisplay();
    }
    this.display.vendors = this.processedVendorList.vendors;
    for (const purposeId of items.purposes) {
      const purpose = this.processedVendorList.purposes[this.idToConsentId(purposeId, PURPOSES_OFFSET)];
      purpose.isConsent = true;
      purposes[purpose.id] = purpose;
    }
    for (const legitimateInterestId of items.legIntPurposes) {
      const purpose = this.processedVendorList.purposes[this.idToConsentId(legitimateInterestId, PURPOSES_OFFSET)];
      purpose.isLegitimateInterest = true;
      purposes[purpose.id] = purpose;
    }

    if (items.stacks) {
      for (const stackId of items.stacks) {
        const stack = this.processedVendorList.stacks[stackId];
        for (const purpose of stack.purposes) {
          purpose.isInStack = true;
        }
        for (const sf of stack.specialFeatures) {
          sf.isInStack = true;
        }
      }
    }

    if (purposes[1] && !purposes[1].isInStack) {
      /**
       * IAB had in their slide the purpose of id 1, always at the top
       * so we ensure this
       */
      this.display.purposes.push(purposes[1]);
      delete purposes[1];
    }

    this.display.purposes = this.display.purposes.concat(Object.values(purposes).filter(p => !p.isInStack));

    this.display.features = items.features.map(
      id => this.processedVendorList.features[this.idToConsentId(id, FEATURES_OFFSET)]
    );

    this.display.specialFeatures = items.specialFeatures
      .map(id => {
        const sf = this.processedVendorList.specialFeatures[this.idToConsentId(id, SPECIAL_FEATURES_OFFSET)];
        sf.isConsent = true;
        return sf;
      })
      .filter(sf => !sf.isInStack);
    this.display.specialPurposes = items.specialPurposes.map(
      id => this.processedVendorList.specialPurposes[this.idToConsentId(id, SPECIAL_PURPOSES_OFFSET)]
    );

    if (items.stacks) {
      this.display.stacks = items.stacks.map(id => this.processedVendorList.stacks[id]);
    }
    return this.display;
  }

  useStackIds(...stackIds) {
    if (!stackIds.length) {
      throw new Error(`useStackIds - Invalid parameters:${JSON.stringify(stackIds)}`);
    }
    const suitableStacks = [];

    let purposeIds = [...this.lastItemToDisplay.purposes, ...this.lastItemToDisplay.legIntPurposes];
    let specialFeatureIds = [...this.lastItemToDisplay.specialFeatures];
    for (const stackId of stackIds) {
      const currentStack = this.processedVendorList.stacks[stackId];
      this.scoreStack(currentStack, purposeIds, specialFeatureIds);
      if (currentStack.isSuitable) {
        suitableStacks.push(stackId);
        const stackPurposes = currentStack.purposes.map(p => p.id);
        purposeIds = purposeIds.filter(id => !stackPurposes.includes(id));
        const stackSF = currentStack.specialFeatures.map(sf => sf.id);
        specialFeatureIds = specialFeatureIds.filter(id => !stackSF.includes(id));
      } else {
        console.warn('Invalid Stack configuration: Stacks will be deactivated.');
        return;
      }
    }

    this.computeDisplay({ ...this.lastItemToDisplay, stacks: suitableStacks });
  }

  scoreStack(stack, purposeIds, specialFeatureIds) {
    const maxDistance = purposeIds.length + specialFeatureIds.length;
    let intersectionSize = 0;
    let stackHasNonIncludedPurpose = false;
    for (const purpose of stack.purposes) {
      if (purposeIds.includes(purpose.id)) {
        intersectionSize++;
      } else {
        stackHasNonIncludedPurpose = true;
        break;
      }
    }
    for (const sf of stack.specialFeatures) {
      if (specialFeatureIds.includes(sf.id)) {
        intersectionSize++;
      } else {
        stackHasNonIncludedPurpose = true;
        break;
      }
    }
    stack.isSuitable = !stackHasNonIncludedPurpose;
    stack.score = maxDistance - intersectionSize;
    return stack;
  }

  sortSuitableStacks(purposeIds, specialFeatureIds) {
    const stacks = Object.values(this.processedVendorList.stacks);

    for (const stack of stacks) {
      this.scoreStack(stack, purposeIds, specialFeatureIds);
    }

    return stacks.filter(s => s.isSuitable).sort((a, b) => a.score - b.score);
  }

  useAutoStack() {
    let purposeIds = [...this.lastItemToDisplay.purposes, ...this.lastItemToDisplay.legIntPurposes];
    let specialFeatureIds = [...this.lastItemToDisplay.specialFeatures];
    const stacks = [];
    let currentStack;
    do {
      currentStack = this.sortSuitableStacks(purposeIds, specialFeatureIds)[0];
      if (currentStack) {
        stacks.push(currentStack);
        const stackPurposes = currentStack.purposes.map(p => p.id);
        purposeIds = purposeIds.filter(id => !stackPurposes.includes(id));
        const stackSF = currentStack.specialFeatures.map(sf => sf.id);
        specialFeatureIds = specialFeatureIds.filter(id => !stackSF.includes(id));
      }
    } while (currentStack);
    if (stacks.length === 0) {
      console.warn('useAutoStack - No suitable stacks were found for the given vendors');
      return;
    }
    this.useStackIds(...stacks.map(s => s.id).sort((a, b) => a - b));
  }
}
